import { BrightcoveVideo } from "@components/Blocks/BrightcoveVideo/BrightcoveVideo";
import ShareSocial, {
  type SocialVariant,
} from "@components/Blocks/ShareSocial/ShareSocial";
import { LabelCardIconVariety } from "@elements/Icon/LabelCardIcon";
import LabelCardIconNew from "@elements/Icon/LabelCardIconNew";
import { NoSSR } from "@sphtech/web2-core/components";
import type { LatestVideoResponse } from "@typings/Brightcove.d";
import { RefObject } from "react";
import { twMerge } from "tailwind-merge";

import PremiumVideoPlayer from "./PremiumVideoPlayer";

type CardStyles = {
  wrapperClass?: string;
  nameClass?: string;
  descriptionClass?: string;
  dateClass?: string;
};

type Props = {
  playing: LatestVideoResponse;
  forwardedRef: RefObject<HTMLDivElement | null>;
  onEnded?: () => void;
  nextPlaylist?: () => void;
  showDescription?: boolean;
  cardStyle?: CardStyles;
  variant?: "video" | "webinar";
};

const socialIconList = ["facebook", "telegram", "whatsapp"];

export default function VideoPlayer({
  playing,
  forwardedRef,
  onEnded,
  nextPlaylist,
  showDescription = false,
  cardStyle = {},
  variant = "video",
}: Props) {
  const {
    id,
    name,
    topStoryDate,
    description,
    published_at,
    webinar_time,
    isPremium,
  } = playing;
  const { wrapperClass, nameClass, descriptionClass, dateClass } = cardStyle;
  return (
    <>
      {isPremium && variant === "webinar" ? (
        <PremiumVideoPlayer
          playing={playing}
          forwardedRef={forwardedRef}
          onEnded={onEnded}
          nextPlaylist={nextPlaylist}
        />
      ) : (
        <BrightcoveVideo
          videoId={id}
          forwardedRef={forwardedRef}
          onEnded={onEnded}
          nextPlaylist={nextPlaylist}
        />
      )}
      <div className="flex flex-col font-secondary">
        <h3
          className={twMerge(
            "font-medium text-[28px] leading-xl text-text-body my-xs",
            nameClass,
          )}
        >
          {name}
        </h3>
        {showDescription && (
          <span
            className={twMerge(
              "my-2 hidden lg:block text-xxs text-semimedium lg:text-xs text-text-muted",
              descriptionClass,
            )}
          >
            {description}
          </span>
        )}
        <div
          className={twMerge("flex items-center justify-between", wrapperClass)}
        >
          <span
            className={twMerge(
              "text-xxs text-semimedium lg:text-base text-text-muted",
              dateClass,
            )}
          >
            {variant === "webinar" ? (
              <>
                {isPremium && (
                  <LabelCardIconNew
                    contentAccess={LabelCardIconVariety.PREMIUM}
                    className="mr-2"
                  />
                )}
                {published_at}{" "}
                <span className="font-light">- {webinar_time}</span>
              </>
            ) : (
              topStoryDate
            )}
          </span>
          <div className="flex gap-2">
            <NoSSR>
              {socialIconList.map((variant, index) => (
                <ShareSocial
                  variant={variant as SocialVariant}
                  pagePath={`videos/${id}`}
                  key={index}
                />
              ))}
            </NoSSR>
          </div>
        </div>
      </div>
    </>
  );
}
